import { useEffect, useRef, useState } from 'react';

import ProductAvailabilityType from '../../../types/ProductAvailabilityType';
import { DEFAULT_MAX_QUANTITY } from '../ProductAddToCart';

const createCartProduct = (id, qty) => ({ id, qty });

const useProductMatrixCell = ({
  accountStore,
  configStore,
  uiStore,
  cellCount,
  onHandleProductQuantity,
  product,
  propertyListRef,
  value: controlledValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [quantity, setQuantity] = useState('');
  const inputRef = useRef(null);

  const isControlled =
    controlledValue !== undefined && controlledValue !== null;
  const displayQuantity = isControlled
    ? controlledValue === false || controlledValue === ''
      ? ''
      : String(controlledValue)
    : quantity;

  useEffect(() => {
    const propertyListElement = propertyListRef?.current;

    if (!propertyListElement) {
      return undefined;
    }

    if (isOpen) {
      propertyListElement.addEventListener('scroll', hideTooltip);
    } else {
      propertyListElement.removeEventListener('scroll', hideTooltip);
    }

    return () => {
      propertyListElement.removeEventListener('scroll', hideTooltip);
    };
  }, [propertyListRef, isOpen]);

  const openPopover = () => setIsOpen(true);

  const closePopover = () => setIsOpen(false);

  function hideTooltip(event) {
    if (inputRef.current && event.type === 'scroll') {
      inputRef.current.blur();
    }
  }

  const getQuantity = (qty, maxQuantity) => {
    const freeQuantity = product.getFreeQuantity(product.id);
    const cannotBeOrdered =
      freeQuantity <= 0 && !product.canBeOrderedOutOfStock && qty < 0;
    const canBeOrdered = freeQuantity > 0 && qty > 0 && qty <= maxQuantity;
    const exceedsMaxQuantity = qty >= maxQuantity;
    const nonNegativeQuantity = qty > 0 && qty;

    if (cannotBeOrdered) {
      return 0;
    }

    if (canBeOrdered) {
      return nonNegativeQuantity;
    }

    return exceedsMaxQuantity ? maxQuantity : nonNegativeQuantity;
  };

  const getMaxByPackage = () =>
    product.sellInPackage
      ? Math.floor(product.getFreeQuantity(product.id) / product.package_size) *
        product.package_size
      : product.getFreeQuantity(product.id);

  const getMaxQuantity = () => {
    const maxAccountQuantity =
      accountStore.account && accountStore.account.max_product_quantity > 0
        ? accountStore.account.max_product_quantity
        : DEFAULT_MAX_QUANTITY;

    const maxBackorderQuantity =
      product.availability_type === ProductAvailabilityType.ALLOW_BACKORDER
        ? configStore.product.backorderLimit > 0
          ? configStore.product.backorderLimit
          : DEFAULT_MAX_QUANTITY
        : DEFAULT_MAX_QUANTITY;

    const maxNormalProductQuantity =
      product.availability_type !== ProductAvailabilityType.ALLOW_BACKORDER
        ? product.canBeOrderedOutOfStock
          ? DEFAULT_MAX_QUANTITY
          : getMaxByPackage()
        : DEFAULT_MAX_QUANTITY;

    return Math.min(
      maxAccountQuantity,
      maxBackorderQuantity,
      maxNormalProductQuantity,
      DEFAULT_MAX_QUANTITY
    );
  };

  const handleProductQuantity = (event) => {
    let maxQuantity = getMaxQuantity();

    maxQuantity = product.countQuantityWithPackageSizeDecimals(maxQuantity);

    const newQuantity = event.target.value
      ? getQuantity(Number(event.target.value), maxQuantity)
      : '';

    onHandleProductQuantity(createCartProduct(product.id, newQuantity));

    if (!isControlled) {
      setQuantity(newQuantity);
    }
  };

  const getCellStyle = () => {
    let width = 100;

    if ((cellCount > 5 && !uiStore.isDesktop) || cellCount > 10) {
      width = 78;
    }

    return {
      width,
    };
  };

  const blockedState = !product
    ? 'missing'
    : !product.available_online
    ? 'unavailable'
    : null;

  return {
    blockedState,
    cellStyle: getCellStyle(),
    closePopover,
    displayQuantity,
    handleProductQuantity,
    inputRef,
    isOpen,
    openPopover,
  };
};

export default useProductMatrixCell;
